/**
 * PreferredShipFrom.jsx - Preferred Ship-From Override module
 * Manages preferred source plants and transport modes for material/customer combinations
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { shipFromAPI } from '../services/api';
import { useToast } from '../components/Toast';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchableDropdown from '../components/SearchableDropdown';
import EditableCell from '../components/EditableCell';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import { formatNumber, getErrorMessage } from '../utils/helpers';
import { loadSelfExcludingFilterOptions } from '../utils/filterOptions';

const PreferredShipFrom = () => {
  const { showSuccess, showError } = useToast();

  const filterOptionKeyByFilter = {
    material_combo: 'material_combos',
    spg: 'spgs',
    sold_to_customer: 'sold_to_customers',
    ship_to_customer: 'ship_to_customers',
    country: 'countries',
    world_region: 'world_regions',
    business_organization: 'business_organizations',
    business_unit_group: 'business_unit_groups',
    business_unit: 'business_units',
    family_description: 'family_descriptions',
    current_pref_plant: 'current_pref_plants',
    current_pref_mode: 'current_pref_modes',
  };
  
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  
  // Edits
  const [pendingEdits, setPendingEdits] = useState({});
  
  // Modal state
  const [showNewCombinationModal, setShowNewCombinationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [plantModeOptions, setPlantModeOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  const [newCombination, setNewCombination] = useState({ plant: '', mode: '' });
  const [submittingNewCombination, setSubmittingNewCombination] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successDetails, setSuccessDetails] = useState({ records: [] });
  const [successActionType, setSuccessActionType] = useState('save'); // 'save' or 'request_new'
  const [showRLSHelpModal, setShowRLSHelpModal] = useState(false);
  
  // Plant and mode options for new combination modal
  const [availablePlants, setAvailablePlants] = useState([]);
  const [availableModes, setAvailableModes] = useState([]);
  const [loadingPlants, setLoadingPlants] = useState(false);

  // AbortController ref to cancel in-flight requests
  const abortControllerRef = useRef(null);

  // Memoized data loading functions
  const loadItems = useCallback(async () => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    setLoading(true);
    setItems([]); // Clear items immediately to prevent showing stale data
    try {
      const data = await shipFromAPI.getItems({ page, page_size: pageSize, ...filters }, signal);
      setItems(data.items);
      setTotalItems(data.total);
      setTotalPages(data.total_pages);
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        showError(getErrorMessage(error));
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [page, pageSize, filters, showError]);

  const loadFilterOptions = useCallback(async () => {
    setFilterOptionsLoading(true);
    try {
      const options = await loadSelfExcludingFilterOptions({
        getFilterOptions: shipFromAPI.getFilterOptions,
        filters,
        filterOptionKeyByFilter,
      });
      setFilterOptions(options);
    } catch (error) {
      console.error('Failed to load filter options:', error);
      showError('Failed to load filter options');
    } finally {
      setFilterOptionsLoading(false);
    }
  }, [filters, showError]);

  // Load data and filter options when page or filters change
  useEffect(() => {
    loadItems();
    loadFilterOptions();
    
    // Cleanup: abort any pending requests on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadItems, loadFilterOptions]);

  // Load transport modes only on initial mount
  useEffect(() => {
    loadTransportModes();
  }, []);

  const loadTransportModes = async () => {
    try {
      const data = await shipFromAPI.getTransportModes();
      setAvailableModes(data.modes || []);
    } catch (error) {
      console.error('Failed to load transport modes:', error);
      showError('Failed to load transport modes');
    }
  };

  const loadPlantOptions = async (material) => {
    setLoadingPlants(true);
    try {
      const data = await shipFromAPI.getPlantOptions(material);
      setAvailablePlants(data.plants || []);
    } catch (error) {
      console.error('Failed to load plant options:', error);
      showError('Failed to load plant options');
    } finally {
      setLoadingPlants(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleCellEdit = async (itemIndex, field, value) => {
    const item = items[itemIndex];
    const editKey = `${item.Material}|${item.ShipToReferenceAddress}`;
    
    setPendingEdits((prev) => ({
      ...prev,
      [editKey]: {
        ...prev[editKey],
        Material: item.Material,
        MaterialWithLeadingZeros: item.MaterialWithLeadingZeros,
        ShipTo: item.ShipToReferenceAddress,
        ShipToWithLeadingZeros: item.ShipToWithLeadingZeros,
        ForecastItemID: item.ForecastItemID,
        [field]: value,
      },
    }));

    // Update local state
    const updatedItems = [...items];
    updatedItems[itemIndex] = { ...updatedItems[itemIndex], [field]: value };
    setItems(updatedItems);
  };

  const handleSaveChanges = async () => {
    const updates = Object.values(pendingEdits);
    if (updates.length === 0) return;

    setSaving(true);
    try {
      await shipFromAPI.bulkUpdate(updates);
      
      // Capture details before clearing
      const savedRecords = updates.map(update => {
        const item = items.find(i => 
          i.Material === update.Material && 
          i.ShipToReferenceAddress === update.ShipTo
        );
        return {
          material: item?.Material,
          soldTo: item?.SoldToReferenceAddress,
          shipTo: item?.ShipToReferenceAddress,
          customerName: item?.CustomerName,
          newPrefPlant: update.OverridePrefShipFrom,
          newPrefMode: update.OverridePrefMode,
          oldPrefPlant: item?.CurrentPrefShipFrom,
          oldPrefMode: item?.CurrentPrefMode
        };
      });
      
      // Optimistically update CurrentPrefShipFrom/Mode in local items state
      // so the display reflects the change immediately without a reload
      setItems(prev => prev.map(item => {
        const update = updates.find(u => u.Material === item.Material && u.ShipTo === item.ShipToReferenceAddress);
        if (!update) return item;
        return {
          ...item,
          CurrentPrefShipFrom: update.OverridePrefShipFrom,
          CurrentPrefMode: update.OverridePrefMode,
        };
      }));

      setPendingEdits({});
      setSaving(false);
      
      // Show success modal
      setSuccessDetails({ records: savedRecords });
      setSuccessActionType('save');
      setShowSuccessModal(true);
    } catch (error) {
      showError(getErrorMessage(error));
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setPendingEdits({});
    loadItems(); // Reload to restore original values
    showSuccess('All pending changes discarded');
  };

  const handleShowOptions = async (item) => {
    const itemKey = `${item.Material}|${item.ShipToReferenceAddress}`;
    
    // Only fetch if we don't have options cached for this item
    if (!plantModeOptions[itemKey] && !loadingOptions[itemKey]) {
      setLoadingOptions(prev => ({ ...prev, [itemKey]: true }));
      try {
        const response = await shipFromAPI.getPlantModeOptions(
          item.Material,
          item.SoldToReferenceAddress,
          item.ShipToReferenceAddress
        );
        setPlantModeOptions(prev => ({
          ...prev,
          [itemKey]: response.options || []
        }));
      } catch (error) {
        console.error('Error loading options:', error);
        showError(getErrorMessage(error));
        setPlantModeOptions(prev => ({
          ...prev,
          [itemKey]: []
        }));
      } finally {
        setLoadingOptions(prev => ({ ...prev, [itemKey]: false }));
      }
    }
  };

  const handleOptionSelect = async (item, value) => {
    if (value === 'NEW_COMBINATION') {
      setSelectedItem(item);
      setNewCombination({ plant: '', mode: '' });
      
      // Load plant options for this material
      await loadPlantOptions(item.Material);
      
      setShowNewCombinationModal(true);
    } else if (value) {
      // Parse the selected option (format: "plant|mode")
      const [plant, mode] = value.split('|');
      const itemIndex = items.findIndex(i => 
        i.Material === item.Material && i.ShipToReferenceAddress === item.ShipToReferenceAddress
      );
      if (itemIndex !== -1) {
        handleCellEdit(itemIndex, 'OverridePrefShipFrom', plant);
        handleCellEdit(itemIndex, 'OverridePrefMode', mode);
      }
    }
  };

  const handleRequestNewCombination = async () => {
    if (!newCombination.plant || !newCombination.mode) {
      showError('Please select both plant and mode');
      return;
    }

    setSubmittingNewCombination(true);
    try {
      console.log('Submitting new combination:', {
        forecastItemId: selectedItem.ForecastItemID,
        material: selectedItem.Material,
        shipTo: selectedItem.ShipToReferenceAddress,
        plant: newCombination.plant,
        mode: newCombination.mode,
      });
      
      await shipFromAPI.requestNewCombination({
        forecastItemId: selectedItem.ForecastItemID,
        material: selectedItem.Material,
        shipTo: selectedItem.ShipToReferenceAddress,
        plant: newCombination.plant,
        mode: newCombination.mode,
      });
      
      console.log('New combination saved successfully');
      setShowNewCombinationModal(false);
      
      // Show success confirmation with details
      setSuccessDetails({
        records: [{
          material: selectedItem.Material,
          customerName: selectedItem.CustomerName,
          shipTo: selectedItem.ShipToReferenceAddress,
          oldPrefPlant: selectedItem.CurrentPrefShipFrom,
          oldPrefMode: selectedItem.CurrentPrefMode,
          newPrefPlant: newCombination.plant,
          newPrefMode: newCombination.mode,
        }]
      });
      setSuccessActionType('request_new');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving new combination:', error);
      showError(getErrorMessage(error));
    } finally {
      setSubmittingNewCombination(false);
    }
  };

  const handleExport = async () => {
    try {
      showSuccess('Preparing export...');
      
      // Call export API with current filters
      const blob = await shipFromAPI.exportToExcel(filters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Preferred_Ship_From_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess('Export completed successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      showError(getErrorMessage(error));
    }
  };

  const pendingCount = Object.keys(pendingEdits).length;

  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Preferred Ship-From Override</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage preferred source plants and transport modes for material/customer combinations
            </p>
          </div>
          <a
            href="https://eastmanchem.sharepoint.com/:p:/r/sites/ibpcoe/Shared%20Documents/Data%20Management/IBP%20Preferred%20Ship%20From%20Override%20QRG.pptx?d=w96d6c65481524436be1fff9b7a5e11d7&csf=1&web=1&e=l3YEBr"
            target="_blank"
            rel="noopener noreferrer"
            className="text-eastman-blue hover:text-blue-700 text-xs sm:text-sm font-medium inline-flex items-center self-start"
          >
            Quick Reference Guide
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-panel">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
            <SearchableDropdown
              options={filterOptions.material_combos || []}
              value={filters.material_combo || ''}
              onChange={(value) => handleFilterChange('material_combo', value)}
              placeholder={filterOptionsLoading ? 'Loading...' : 'All Materials'}
              disabled={filterOptionsLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sold To Customer</label>
            <SearchableDropdown
              options={filterOptions.sold_to_customers || []}
              value={filters.sold_to_customer || ''}
              onChange={(value) => handleFilterChange('sold_to_customer', value)}
              placeholder={filterOptionsLoading ? 'Loading...' : 'All Sold To Customers'}
              disabled={filterOptionsLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ship To Customer</label>
            <SearchableDropdown
              options={filterOptions.ship_to_customers || []}
              value={filters.ship_to_customer || ''}
              onChange={(value) => handleFilterChange('ship_to_customer', value)}
              placeholder={filterOptionsLoading ? 'Loading...' : 'All Ship To Customers'}
              disabled={filterOptionsLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <SearchableDropdown
              options={filterOptions.countries || []}
              value={filters.country || ''}
              onChange={(value) => handleFilterChange('country', value)}
              placeholder={filterOptionsLoading ? 'Loading...' : 'All Countries'}
              disabled={filterOptionsLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Unit Group</label>
            <SearchableDropdown
              options={filterOptions.business_unit_groups || []}
              value={filters.business_unit_group || ''}
              onChange={(value) => handleFilterChange('business_unit_group', value)}
              placeholder={filterOptionsLoading ? 'Loading...' : 'All Business Unit Groups'}
              disabled={filterOptionsLoading}
            />
          </div>
        </div>

        {/* Additional Filters Toggle */}
        <div className="mt-3">
          <button
            onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
            className="text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            {showAdditionalFilters ? '▼' : '▶'} Additional Filters
          </button>
        </div>

        {/* Additional Filters Section */}
        {showAdditionalFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ship To World Region</label>
                <SearchableDropdown
                  options={filterOptions.world_regions || []}
                  value={filters.world_region || ''}
                  onChange={(value) => handleFilterChange('world_region', value)}
                  placeholder={filterOptionsLoading ? 'Loading...' : 'All Regions'}
                  disabled={filterOptionsLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Organization</label>
                <SearchableDropdown
                  options={filterOptions.business_organizations || []}
                  value={filters.business_organization || ''}
                  onChange={(value) => handleFilterChange('business_organization', value)}
                  placeholder={filterOptionsLoading ? 'Loading...' : 'All Organizations'}
                  disabled={filterOptionsLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Family Description</label>
                <SearchableDropdown
                  options={filterOptions.family_descriptions || []}
                  value={filters.family_description || ''}
                  onChange={(value) => handleFilterChange('family_description', value)}
                  placeholder={filterOptionsLoading ? 'Loading...' : 'All Families'}
                  disabled={filterOptionsLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SPG</label>
                <SearchableDropdown
                  options={filterOptions.spgs || []}
                  value={filters.spg || ''}
                  onChange={(value) => handleFilterChange('spg', value)}
                  placeholder={filterOptionsLoading ? 'Loading...' : 'All SPGs'}
                  disabled={filterOptionsLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Pref Plant</label>
                <SearchableDropdown
                  options={filterOptions.current_pref_plants || []}
                  value={filters.current_pref_plant || ''}
                  onChange={(value) => handleFilterChange('current_pref_plant', value)}
                  placeholder={filterOptionsLoading ? 'Loading...' : 'All Plants'}
                  disabled={filterOptionsLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Pref Mode</label>
                <SearchableDropdown
                  options={filterOptions.current_pref_modes || []}
                  value={filters.current_pref_mode || ''}
                  onChange={(value) => handleFilterChange('current_pref_mode', value)}
                  placeholder={filterOptionsLoading ? 'Loading...' : 'All Modes'}
                  disabled={filterOptionsLoading}
                />
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <div className="space-x-2">
            <button onClick={clearFilters} className="btn btn-secondary btn-sm">
              Clear Filters
            </button>
            <button onClick={handleExport} className="btn btn-secondary btn-sm">
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export to Excel
            </button>
          </div>
          {pendingCount > 0 && (
            <div className="space-x-2">
              <button onClick={handleDiscardChanges} className="btn btn-secondary btn-sm" disabled={saving}>
                Discard {pendingCount} Changes
              </button>
              <button onClick={handleSaveChanges} className="btn btn-primary btn-sm" disabled={saving}>
                {saving ? 'Saving...' : `Save ${pendingCount} Changes`}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading records...</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '200px' }}>Forecast Item ID</th>
                  <th style={{ width: '90px' }}>Material</th>
                  <th style={{ width: '180px' }}>Material Description</th>
                  <th style={{ width: '90px' }}>Ship To</th>
                  <th style={{ width: '180px' }}>Customer Name</th>
                  <th style={{ width: '110px' }}>Country</th>
                  <th style={{ width: '75px' }}>Current Pref Plant</th>
                  <th style={{ width: '75px' }}>Current Pref Mode</th>
                  <th style={{ width: '180px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-12">
                      <p className="text-gray-500 mb-3">No records found.</p>
                      <button
                        onClick={() => setShowRLSHelpModal(true)}
                        className="text-eastman-blue hover:text-blue-700 text-sm font-medium inline-flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Why am I seeing no results?
                      </button>
                    </td>
                  </tr>
                )}
                {items.map((item, index) => {
                  const editKey = `${item.Material}|${item.ShipToReferenceAddress}`;
                  const hasEdits = pendingEdits[editKey];
                  const selectedValue = hasEdits 
                    ? `${pendingEdits[editKey].OverridePrefShipFrom}|${pendingEdits[editKey].OverridePrefMode}`
                    : '';
                  return (
                    <tr key={index} className={hasEdits ? 'edited' : ''}>
                      <td style={{ width: '200px', wordWrap: 'break-word', whiteSpace: 'normal' }}>{item.ForecastItemID}</td>
                      <td style={{ width: '90px' }}>{item.Material}</td>
                      <td style={{ width: '180px', wordWrap: 'break-word', whiteSpace: 'normal' }}>{item.MaterialDescription}</td>
                      <td style={{ width: '90px' }}>{item.ShipToReferenceAddress}</td>
                      <td style={{ width: '180px', wordWrap: 'break-word', whiteSpace: 'normal' }}>{item.CustomerName}</td>
                      <td style={{ width: '110px', wordWrap: 'break-word', whiteSpace: 'normal'  }}>{item.Country}</td>
                      <td style={{ width: '75px' }}>{item.CurrentPrefShipFrom}</td>
                      <td style={{ width: '75px' }}>{item.CurrentPrefMode}</td>
                      <td style={{ width: '180px' }}>
                        <select
                          className="select text-sm"
                          style={{ width: '170px' }}
                          onChange={(e) => handleOptionSelect(item, e.target.value)}
                          onClick={() => handleShowOptions(item)}
                          value={selectedValue}
                        >
                          <option value="">
                            {loadingOptions[`${item.Material}|${item.ShipToReferenceAddress}`] 
                              ? 'Loading options...' 
                              : 'Select Plant/Mode...'}
                          </option>
                          {(plantModeOptions[`${item.Material}|${item.ShipToReferenceAddress}`] || []).map((option, idx) => (
                            <option key={idx} value={`${option.ShipFromPlant}|${option.TransportMode}`}>
                              {option.ShipFromPlant} - {option.TransportMode}
                            </option>
                          ))}
                          {!loadingOptions[`${item.Material}|${item.ShipToReferenceAddress}`] && (
                            <option value="NEW_COMBINATION">Request New Combination...</option>
                          )}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            itemsPerPage={pageSize}
            totalItems={totalItems}
          />
        </>
      )}

      {/* Request New Combination Modal */}
      <Modal
        isOpen={showNewCombinationModal}
        onClose={() => setShowNewCombinationModal(false)}
        title="Request New Plant/Mode Combination"
      >
        {selectedItem && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Requesting new combination for: <br />
              <span className="font-medium">{selectedItem.ForecastItemID}</span>
            </p>
            
            {/* Informational message about plant availability */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Plant options shown below are filtered based on active status for this material. 
                To request an active status, please visit: <br />
                <a 
                  href="https://eastmanchem.sharepoint.com/sites/MasterData/SitePages/GMN-Creation.aspx?OR=Teams-HL&CT=1707746283147&xsdata=MDV8MDJ8QU1hY3JvcmllQGVhc3RtYW4uY29tfGYwOWU0YWE3MDBkZDQ0NGFlZWMxMDhkZTc5MmZiMjcyfDliNTBjODBmMTAzYjQ0MzNhOTFjYjRiMDZjNTA3Mzg3fDB8MHw2MzkwODE0NDM1Nzk5ODU1MDl8VW5rbm93bnxUV0ZwYkdac2IzZDhleUpGYlhCMGVVMWhjR2tpT25SeWRXVXNJbFlpT2lJd0xqQXVNREF3TUNJc0lsQWlPaUpYYVc0ek1pSXNJa0ZPSWpvaVRXRnBiQ0lzSWxkVUlqb3lmUT09fDB8fHw%3d&sdata=ZnFLSkJmNGdkWHJJZWJiNXMxUlVtRmsvNHIrUGpjYWRua1VGbDJ1YS82WT0%3d"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  GMN Creation
                </a>
              </p>
            </div>
            
            {loadingPlants ? (
              <div className="text-sm text-gray-500">Loading plant options...</div>
            ) : availablePlants.length > 0 ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Requested Plant *
                  </label>
                  <select
                    className="select text-sm w-full"
                    value={newCombination.plant}
                    onChange={(e) => setNewCombination({ ...newCombination, plant: e.target.value })}
                  >
                    <option value="">Select plant...</option>
                    {availablePlants.map((plant) => (
                      <option key={plant} value={plant}>
                        {plant}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Requested Transport Mode *
                  </label>
                  <select
                    className="select text-sm w-full"
                    value={newCombination.mode}
                    onChange={(e) => setNewCombination({ ...newCombination, mode: e.target.value })}
                  >
                    <option value="">Select transport mode...</option>
                    {availableModes.map((mode) => (
                      <option key={mode} value={mode}>
                        {mode}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : null}
            
            {/* Show warning if no plants available */}
            {availablePlants.length === 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-semibold text-amber-800 mb-1">No active plants found for this material</h4>
                    <p className="text-sm text-amber-700 mb-2">
                      Consider requesting inactivation of this forecast item.
                    </p>
                    
                    <p className="text-sm text-amber-700 mt-2">
                      If activation is still needed, use the link above to request plant activation.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowNewCombinationModal(false)}
                className="btn btn-secondary"
                disabled={submittingNewCombination}
              >
                Cancel
              </button>
              {availablePlants.length > 0 && !loadingPlants && (
                <button
                  onClick={handleRequestNewCombination}
                  className="btn btn-primary"
                  disabled={!newCombination.plant || !newCombination.mode || submittingNewCombination}
                >
                  {submittingNewCombination ? 'Saving...' : 'Save Combination'}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
      
      {/* RLS Access Help Modal */}
      <Modal
        isOpen={showRLSHelpModal}
        onClose={() => setShowRLSHelpModal(false)}
        title="Why Am I Seeing No Results?"
        size="md"
      >
        <div className="space-y-4 py-2">
          <div className="flex items-start space-x-3">
            <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm text-gray-700">
                You may be seeing zero results because you do not have <strong>Row-Level Security (RLS)</strong> access to the forecast data for your business and/or regions.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <p className="text-sm font-semibold text-blue-900">How to request access:</p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
              <li>
                Visit the Access Center:{' '}
                <a
                  href="https://accesscenter.emn.com/bisecurity"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:text-blue-600 inline-flex items-center"
                >
                  accesscenter.emn.com/bisecurity
                  <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </li>
              <li>Request access to the topic area <strong>"Forecast Analysis"</strong></li>
              <li>Select the appropriate <strong>business</strong> and/or <strong>regions</strong> you need access to</li>
            </ol>
          </div>

          <p className="text-xs text-gray-500">
            If you believe you already have access and are still seeing no results, try clearing any active filters or contact{' '}
            <a href="mailto:IBPDataManagement@eastman.com" className="underline hover:text-gray-700">IBPDataManagement@eastman.com</a>.
          </p>
        </div>
        <div className="mt-6 flex justify-end">
          <button onClick={() => setShowRLSHelpModal(false)} className="btn btn-primary">
            Got it
          </button>
        </div>
      </Modal>

      {/* Success Confirmation Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          loadItems();
        }}
        title={successActionType === 'request_new' ? 'New Combination Request Submitted!' : 'Preferred Ship-From Updated Successfully!'}
        size="lg"
      >
        <div className="py-4">
          {successActionType === 'request_new' ? (
            <div className="mb-4">
              <p className="text-gray-700 font-medium mb-2">
                Your request has been successfully submitted!
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  The preferred ship-from override will be automatically updated and will be effective in OMP within <strong>2-4 business days</strong>.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-700 mb-4">
              Successfully updated preferred ship-from for {successDetails.records.length} item(s) in the database.
            </p>
          )}
          
          <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Material</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Customer</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Ship To</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    {successActionType === 'request_new' ? 'Current' : 'Previous'}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    {successActionType === 'request_new' ? 'Requested' : 'New'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {successDetails.records.map((record, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap font-mono">{record.material}</td>
                    <td className="px-3 py-2">{record.customerName}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{record.shipTo}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="text-gray-500">
                        <div><span className="font-semibold">Plant:</span> {record.oldPrefPlant || 'None'}</div>
                        <div><span className="font-semibold">Mode:</span> {record.oldPrefMode || 'None'}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className={successActionType === 'request_new' ? 'text-blue-700 font-semibold' : 'text-green-700 font-semibold'}>
                        <div><span className="font-normal">Plant:</span> {record.newPrefPlant}</div>
                        <div><span className="font-normal">Mode:</span> {record.newPrefMode}</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={() => {
              setShowSuccessModal(false);
              loadItems();
            }}
            className="btn btn-primary"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PreferredShipFrom;
